import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from "firebase/firestore";

import { auth, db } from "../../shared/libs/firebaseConfig";
import {
  clearAffiliateAttributionForCourse,
  isAffiliateAttributionUsable,
  readAffiliateAttribution,
} from "../../shared/utils/affiliateAttribution";
import { normalizeAffiliateCommissionPercent } from "../../shared/utils/affiliateSchema";

import "./CourseEnrollPage.css";

// --------------------------------------------------
// helper: package options สำหรับคอร์สเนติบัณฑิต
// --------------------------------------------------
const THAIBAR_PACKAGE_OPTIONS = [
  { value: "F_2", label: "การันตีคืนเงิน (ยาว+สรุป) 2 ขา" },
  { value: "F_1_civil", label: "การันตีคืนเงิน (ยาว+สรุป) ขาวิแพ่ง 1 ขา" },
  { value: "F_1_criminal", label: "การันตีคืนเงิน (ยาว+สรุป) ขาวิอาญา 1 ขา" },

  { value: "P_2", label: "เพอร์เฟค (ยาว+สรุป+ประมวล) 2 ขา" },
  { value: "P_1_civil", label: "เพอร์เฟค (ยาว+สรุป+ประมวล) ขาวิแพ่ง 1 ขา" },
  { value: "P_1_criminal", label: "เพอร์เฟค (ยาว+สรุป+ประมวล) ขาวิอาญา 1 ขา" },

  { value: "L_2", label: "แม่นหลัก (ยาว) 2 ขา" },
  { value: "L_1_civil", label: "แม่นหลัก (ยาว) ขาวิแพ่ง 1 ขา" },
  { value: "L_1_criminal", label: "แม่นหลัก (ยาว) ขาวิอาญา 1 ขา" },

  { value: "S_2", label: "สรุปก่อนสอบ (สรุป) 2 ขา" },
  { value: "S_1_civil", label: "สรุปก่อนสอบ (สรุป) ขาวิแพ่ง 1 ขา" },
  { value: "S_1_criminal", label: "สรุปก่อนสอบ (สรุป) ขาวิอาญา 1 ขา" },
];

const THAIBAR_PACKAGE_GROUPS = [
  {
    key: "guarantee",
    title: "คอร์สการันตีคืนเงิน",
    items: ["F_2", "F_1_civil", "F_1_criminal"],
  },
  {
    key: "perfect",
    title: "คอร์สเพอร์เฟค",
    items: ["P_2", "P_1_civil", "P_1_criminal"],
  },
  {
    key: "long",
    title: "คอร์สแม่นหลัก",
    items: ["L_2", "L_1_civil", "L_1_criminal"],
  },
  {
    key: "summary",
    title: "คอร์สสรุปก่อนสอบ",
    items: ["S_2", "S_1_civil", "S_1_criminal"],
  },
];

function formatBaht(value) {
  const n = Number(value || 0);
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    maximumFractionDigits: 0,
  }).format(n);
}

function safeNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function getPackageLabel(packageCode) {
  return (
    THAIBAR_PACKAGE_OPTIONS.find((x) => x.value === packageCode)?.label || ""
  );
}

function getSelectedPrice(course, selectedPackage) {
  if (!course) return 0;

  const packagePrices = course.packagePrices || {};
  const pkgRow = packagePrices?.[selectedPackage];

  if (selectedPackage && pkgRow != null) {
    if (typeof pkgRow === "number" || typeof pkgRow === "string") {
      return safeNumber(pkgRow, 0);
    }
    if (typeof pkgRow === "object") {
      if (pkgRow?.price != null) return safeNumber(pkgRow.price, 0);
      if (pkgRow?.amount != null) return safeNumber(pkgRow.amount, 0);
    }
  }

  if (course.price != null) {
    return safeNumber(course.price, 0);
  }

  if (typeof course.priceText === "string") {
    const parsed = Number(course.priceText.replace(/[^\d.]/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
}

function getSelectedPriceText(course, selectedPackage) {
  if (!course) return formatBaht(0);

  const packagePrices = course.packagePrices || {};
  const pkgRow = packagePrices?.[selectedPackage];

  if (selectedPackage && pkgRow != null) {
    if (typeof pkgRow === "object" && pkgRow?.priceText) {
      return String(pkgRow.priceText);
    }
    const n = getSelectedPrice(course, selectedPackage);
    return formatBaht(n);
  }

  if (course?.priceText) return String(course.priceText);
  return formatBaht(getSelectedPrice(course, selectedPackage));
}

function getAllowedHeaderTypesFromPackage(pkg) {
  switch (pkg) {
    case "F_2":
    case "P_2":
      return ["civil", "criminal", "summary_civil", "summary_criminal"];

    case "F_1_civil":
    case "P_1_civil":
      return ["civil", "summary_civil"];

    case "F_1_criminal":
    case "P_1_criminal":
      return ["criminal", "summary_criminal"];

    case "L_2":
      return ["civil", "criminal"];
    case "L_1_civil":
      return ["civil"];
    case "L_1_criminal":
      return ["criminal"];

    case "S_2":
      return ["summary_civil", "summary_criminal"];
    case "S_1_civil":
      return ["summary_civil"];
    case "S_1_criminal":
      return ["summary_criminal"];

    default:
      return [];
  }
}

function formatPaidAt(value) {
  try {
    const d =
      value?.toDate?.() ||
      (value instanceof Date ? value : value ? new Date(value) : null);
    if (!d || Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("th-TH", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function formatPhoneForDisplay(phone = "") {
  if (phone.startsWith("+66")) {
    return "0" + phone.slice(3);
  }
  return phone;
}

export default function CourseEnrollPage() {
  const navigate = useNavigate();
  const { courseId } = useParams();

  const [uid, setUid] = useState(undefined);

  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const [course, setCourse] = useState(null);
  const [userDoc, setUserDoc] = useState(null);
  const [existingEnrolls, setExistingEnrolls] = useState([]);

  const [submitted, setSubmitted] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [submitError, setSubmitError] = useState("");

  const accountEmail = auth.currentUser?.email || userDoc?.email || "";

  // ทดสอบชำระเงินไปยัง QR - Personal หลังจากทดสอบเสร็จ ใส่ comment
/*   const roleSet = useMemo(() => {
  const roles = Array.isArray(userDoc?.role)
      ? userDoc.role
      : [userDoc?.role].filter(Boolean);
    return new Set(roles.map((x) => String(x).toLowerCase()));
  }, [userDoc]);

  const isAdminUser = roleSet.has("admin") || roleSet.has("creator"); */
  ///////////////////////////////////////////////////////////////

  const [form, setForm] = useState({
    fullName: "",
    phoneNumber: "",
    shippingAddress: "",
    coursePackage: "",
    acceptTerms: false,
  });

  // --------------------------------------------------
  // auth
  // --------------------------------------------------
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setUid(user?.uid || null);
    });
    return unsub;
  }, []);

  // --------------------------------------------------
  // load course + user profile + existing enrolls
  // --------------------------------------------------
  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      if (!courseId) {
        setLoadError("ไม่พบ courseId");
        setLoadingPage(false);
        return;
      }

      if (uid === undefined) return;

      setLoadingPage(true);
      setLoadError("");

      try {
        const courseRef = doc(db, "courses", courseId);
        const courseSnap = await getDoc(courseRef);

        if (!courseSnap.exists()) {
          throw new Error("ไม่พบข้อมูลคอร์ส");
        }

        const courseData = { id: courseSnap.id, ...courseSnap.data() };

        let userData = null;
        if (uid) {
          const userRef = doc(db, "users", uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            userData = { id: userSnap.id, ...userSnap.data() };
          }
        }

        let enrollRows = [];
        if (uid) {
          const enrollQ = query(
            collection(db, "enrolls"),
            where("userId", "==", uid),
            where("courseId", "==", courseId)
          );
          const enrollSnap = await getDocs(enrollQ);
          enrollRows = enrollSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        }

        if (!isMounted) return;

        setCourse(courseData);
        setUserDoc(userData);
        setExistingEnrolls(enrollRows);

        const alreadyGrantedPackages = new Set(
          enrollRows
            .filter((x) => x?.accessStatus === "granted" && x?.coursePackage)
            .map((x) => x.coursePackage)
        );

        const availablePackages = THAIBAR_PACKAGE_OPTIONS.filter(
          (x) => !alreadyGrantedPackages.has(x.value)
        );

        const defaultCoursePackage =
          courseData.courseType === "thaibar"
            ? availablePackages[0]?.value ||
              courseData.defaultCoursePackage ||
              courseData.defaultPackage ||
              ""
            : "";

        setForm((prev) => ({
          ...prev,
          fullName:
            userData?.enrollProfile?.fullName ||
            userData?.displayName ||
            prev.fullName ||
            "",
          phoneNumber:
            userData?.enrollProfile?.phoneNumber ||
            userData?.phoneNumber ||
            prev.phoneNumber ||
            "",
          shippingAddress:
            userData?.enrollProfile?.shippingAddress ||
            userData?.shippingAddress ||
            prev.shippingAddress ||
            "",
          coursePackage:
            courseData.courseType === "thaibar"
              ? prev.coursePackage &&
                availablePackages.some((x) => x.value === prev.coursePackage)
                ? prev.coursePackage
                : defaultCoursePackage
              : "",
        }));
      } catch (err) {
        console.error(err);
        if (!isMounted) return;
        setLoadError(err?.message || "โหลดข้อมูลไม่สำเร็จ");
      } finally {
        if (isMounted) setLoadingPage(false);
      }
    }

    loadData();

    return () => {
      isMounted = false;
    };
  }, [courseId, uid]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const isThaibar = course?.courseType === "thaibar";

  const grantedEnrolls = useMemo(() => {
    return existingEnrolls.filter((x) => x?.accessStatus === "granted");
  }, [existingEnrolls]);

  const pendingEnrolls = useMemo(() => {
    return existingEnrolls.filter((x) => x?.accessStatus !== "granted");
  }, [existingEnrolls]);

  const purchasedPackageSet = useMemo(() => {
    return new Set(
      grantedEnrolls
        .map((x) => x?.coursePackage)
        .filter(Boolean)
    );
  }, [grantedEnrolls]);

  const availableThaibarPackages = useMemo(() => {
    if (!isThaibar) return [];
    return THAIBAR_PACKAGE_OPTIONS.filter(
      (pkg) => !purchasedPackageSet.has(pkg.value)
    );
  }, [isThaibar, purchasedPackageSet]);

  const availableThaibarPackageGroups = useMemo(() => {
    if (!isThaibar) return [];

    return THAIBAR_PACKAGE_GROUPS.map((group) => {
      const items = group.items
        .map((value) => availableThaibarPackages.find((pkg) => pkg.value === value))
        .filter(Boolean);

      return {
        ...group,
        items,
      };
    }).filter((group) => group.items.length > 0);
  }, [isThaibar, availableThaibarPackages]);

  const isAdditionalPurchase = grantedEnrolls.length > 0;

  const selectedPackageLabel = useMemo(() => {
    if (!isThaibar) return "";
    return getPackageLabel(form.coursePackage);
  }, [isThaibar, form.coursePackage]);

  const selectedPrice = useMemo(() => {
    return getSelectedPrice(course, form.coursePackage);
  }, [course, form.coursePackage]);

  const displayCourseTitle = course?.title || "คอร์สเรียน";
  const displayPriceText = useMemo(() => {
    if (isThaibar) return getSelectedPriceText(course, form.coursePackage);
    return selectedPrice > 0
      ? formatBaht(selectedPrice)
      : course?.priceText || formatBaht(0);
  }, [isThaibar, selectedPrice, course, form.coursePackage]);

  const validation = useMemo(() => {
    const errors = {};

    if (!uid) errors.auth = "กรุณาเข้าสู่ระบบก่อนสมัครเรียน";
    if (!form.fullName.trim()) errors.fullName = "กรุณากรอกชื่อ-นามสกุล";
    if (!form.phoneNumber.trim()) errors.phoneNumber = "กรุณากรอกเบอร์โทรศัพท์";
    if (!form.shippingAddress.trim()) {
      errors.shippingAddress = "กรุณากรอกที่อยู่สำหรับรับเอกสาร";
    }

    if (isThaibar) {
      if (!availableThaibarPackages.length) {
        errors.coursePackage = "คุณมีสิทธิครบทุกแพ็กเกจที่เปิดขายแล้ว";
      } else if (!form.coursePackage) {
        errors.coursePackage = "กรุณาเลือกแพ็กเกจคอร์ส";
      } else if (purchasedPackageSet.has(form.coursePackage)) {
        errors.coursePackage = "แพ็กเกจนี้คุณสมัครแล้ว";
      }
    }

    if (!form.acceptTerms) {
      errors.acceptTerms = "กรุณายอมรับข้อกำหนดและนโยบายความเป็นส่วนตัว";
    }

    return errors;
  }, [uid, form, isThaibar, availableThaibarPackages.length, purchasedPackageSet]);

  const hasError = Object.keys(validation).length > 0;

  const createEnrollAndNavigate = async ({ paymentRoute = "default" } = {}) => {
    setSubmitted(true);
    setSubmitError("");

    if (hasError) return;

    if (!course?.id) {
      setSubmitError("ไม่พบข้อมูลคอร์ส");
      return;
    }

    try {
      setLoadingSubmit(true);

      const currentUser = auth.currentUser;
      if (!currentUser?.uid) {
        throw new Error("กรุณาเข้าสู่ระบบก่อนสมัครเรียน");
      }

      const userRef = doc(db, "users", currentUser.uid);
      const courseRef = doc(db, "courses", course.id);
      const enrollsRef = collection(db, "enrolls");

      const finalCoursePackage = isThaibar ? form.coursePackage : "";
      const finalPackageLabel = isThaibar
        ? getPackageLabel(finalCoursePackage)
        : "";

      if (isThaibar && purchasedPackageSet.has(finalCoursePackage)) {
        throw new Error("แพ็กเกจนี้คุณสมัครแล้ว");
      }

      const amount = selectedPrice;
      const allowedHeaderTypes = isThaibar
        ? getAllowedHeaderTypesFromPackage(finalCoursePackage)
        : [];

      const enrollPayload = {
        userId: currentUser.uid,
        courseId: course.id,

        courseTitle: course.title || "",
        courseSlug: course.slug || "",
        courseType: course.courseType || "general",

        coursePackage: finalCoursePackage || "",
        coursePackageLabel: finalPackageLabel || "",

        allowedHeaderTypes,

        amount,
        amountText: displayPriceText,

        status: "pending_payment",
        paymentStatus: "pending",
        accessStatus: "pending",
        purchaseMode: isAdditionalPurchase ? "additional" : "initial",

        userSnapshot: {
          fullName: form.fullName.trim(),
          email: currentUser.email || userDoc?.email || "",
          phoneNumber: form.phoneNumber.trim(),
          shippingAddress: form.shippingAddress.trim(),
        },

        courseSnapshot: {
          title: course.title || "",
          coverImage:
            course.coverImage ||
            course.imageCoverUrl ||
            course.thumbnailURL ||
            course.thumbnail ||
            "",
          price: course.price ?? null,
          priceText: course.priceText || "",
        },

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const storedAffiliateAttribution = readAffiliateAttribution(course.id);
      const hasUsableAffiliateAttribution = isAffiliateAttributionUsable(
        storedAffiliateAttribution,
        course.id
      );
      let shouldClearAffiliateAttribution =
        !!storedAffiliateAttribution && !hasUsableAffiliateAttribution;

      const enrollRef = await runTransaction(db, async (tx) => {
        const userSnap = await tx.get(userRef);
        const courseSnap = await tx.get(courseRef);

        if (!courseSnap.exists()) {
          throw new Error("ไม่พบข้อมูลคอร์ส");
        }

        const latestCourse = { id: courseSnap.id, ...courseSnap.data() };
        const finalEnrollPayload = { ...enrollPayload };

        if (hasUsableAffiliateAttribution) {
          if (latestCourse?.affiliateEnabled === true) {
            finalEnrollPayload.affiliate = {
              code: storedAffiliateAttribution.code || "",
              affiliateId: storedAffiliateAttribution.affiliateId || "",
              affiliateUserId: storedAffiliateAttribution.affiliateUserId || "",
              source: "url_ref",
              capturedAt: storedAffiliateAttribution.capturedAt || null,
              enrolledAt: Date.now(),
              attributionVersion: 1,
              eligible: true,
              courseAffiliateEnabledAtPurchase: true,
              commissionPercentAtPurchase: normalizeAffiliateCommissionPercent(
                latestCourse.affiliateCommissionPercent,
                10
              ),
            };
            finalEnrollPayload.affiliateCommissionCreated = false;
          } else {
            shouldClearAffiliateAttribution = true;
          }
        }

        const prevUser = userSnap.exists() ? userSnap.data() : {};
        const newEnrollRef = doc(enrollsRef);

        tx.set(
          userRef,
          {
            uid: currentUser.uid,
            email: currentUser.email || prevUser?.email || "",
            displayName:
              form.fullName.trim() ||
              prevUser?.displayName ||
              currentUser.displayName ||
              "",
            shippingAddress: form.shippingAddress.trim(),

            enrollProfile: {
              fullName: form.fullName.trim(),
              phoneNumber: form.phoneNumber.trim(),
              shippingAddress: form.shippingAddress.trim(),
              updatedAt: serverTimestamp(),
            },

            updatedAt: serverTimestamp(),
            createdAt: userSnap.exists()
              ? prevUser?.createdAt || serverTimestamp()
              : serverTimestamp(),
          },
          { merge: true }
        );

        tx.set(newEnrollRef, finalEnrollPayload);

        return newEnrollRef;
      });

      if (shouldClearAffiliateAttribution || hasUsableAffiliateAttribution) {
        clearAffiliateAttributionForCourse(course.id);
      }

      if (paymentRoute === "promptpay_personal") {
        navigate(`/courses/${course.id}/payment-promptpay/${enrollRef.id}`);
        return;
      }

      // แบบเดิม ใช้ QR แม่มณี
      // navigate(`/courses/${course.id}/payment/${enrollRef.id}`);
      navigate(`/courses/${course.id}/payment-promptpay/${enrollRef.id}`);
    } catch (err) {
      console.error(err);
      setSubmitError(err?.message || "สมัครเรียนไม่สำเร็จ");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    await createEnrollAndNavigate({ paymentRoute: "default" });
  };

  const onAdminTestPromptPay = async () => {
    await createEnrollAndNavigate({ paymentRoute: "promptpay_personal" });
  };

  if (loadingPage) {
    return (
      <div className="container py-4">
        <div className="d-flex align-items-center gap-2 text-secondary">
          <div className="spinner-border spinner-border-sm" role="status" />
          <span>กำลังโหลดข้อมูล...</span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="container py-4">
        <Link to={'/courses/${courseId}'} className="text-decoration-none">
          ← กลับไปหน้าคอร์สทั้งหมด
        </Link>

        <div className="alert alert-danger mt-3 mb-0">{loadError}</div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="mb-4">
       {/*  <Link to={`/courses/${courseId}`} className="text-decoration-none">
          ← กลับไปหน้ารายละเอียดคอร์ส
        </Link> */}

        <h1 className="h4 mt-2 mb-1">
          {isAdditionalPurchase ? "สมัครเรียนเพิ่ม" : "สมัครเรียนคอร์ส"}
        </h1>
        <div className="text-secondary">{displayCourseTitle}</div>
      </div>

      <div className="row g-4 align-items-start">
        <div className="col-lg-7">
          <div className="card app-card course-enroll-card">
            <div className="card-body p-4">
              <form onSubmit={onSubmit}>
                <div className="mb-4">
                  <h2 className="h6 mb-3">ข้อมูลผู้สมัครเรียน</h2>

                  {!uid && (
                    <div className="alert alert-warning">
                      กรุณาเข้าสู่ระบบก่อนสมัครเรียน
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">ชื่อ-นามสกุล *</label>
                    <input
                      className={`form-control ${
                        submitted && validation.fullName ? "is-invalid" : ""
                      }`}
                      value={form.fullName}
                      onChange={(e) => setField("fullName", e.target.value)}
                      placeholder="กรอกชื่อ-นามสกุล"
                    />
                    <div className="invalid-feedback">{validation.fullName}</div>
                  </div>

                  {/* <div className="mb-3">
                    <label className="form-label">อีเมล</label>
                    <input
                      className="form-control"
                      value={auth.currentUser?.email || userDoc?.email || ""}
                      disabled
                      placeholder="อีเมลจากบัญชีผู้ใช้งาน"
                    />
                    <div className="form-text">
                      ใช้อีเมลจากบัญชีที่คุณเข้าสู่ระบบ
                    </div>
                  </div> */}

                  {accountEmail && (
                    <div className="mb-3">
                        <label className="form-label">อีเมล</label>
                        <input
                        className="form-control"
                        value={accountEmail}
                        disabled
                        placeholder="อีเมลจากบัญชีผู้ใช้งาน"
                        />
                        <div className="form-text">
                        ใช้อีเมลจากบัญชีที่คุณเข้าสู่ระบบ
                        </div>
                    </div>
                    )}

                  <div className="mb-3">
                    <label className="form-label">เบอร์โทรศัพท์ *</label>
                    <input
                        className={`form-control ${
                        submitted && validation.phoneNumber ? "is-invalid" : ""
                        }`}
                        value={formatPhoneForDisplay(form.phoneNumber || "")}
                        onChange={(e) => setField("phoneNumber", e.target.value)}
                        placeholder="0xx-xxx-xxxx"
                    />
                    <div className="invalid-feedback">
                        {validation.phoneNumber}
                    </div>
                  </div>

                  <div className="mb-0">
                    <label className="form-label">ที่อยู่สำหรับรับเอกสาร (ถ้ามี) *</label>
                    <textarea
                      className={`form-control ${
                        submitted && validation.shippingAddress
                          ? "is-invalid"
                          : ""
                      }`}
                      rows={4}
                      value={form.shippingAddress}
                      onChange={(e) => setField("shippingAddress", e.target.value)}
                      placeholder="บ้านเลขที่ / หมู่ / ถนน / แขวง-ตำบล / เขต-อำเภอ / จังหวัด / รหัสไปรษณีย์"
                    />
                    <div className="invalid-feedback">
                      {validation.shippingAddress}
                    </div>
                  </div>
                </div>

                {isThaibar && grantedEnrolls.length > 0 && (
                  <div className="mb-4">
                    <h2 className="h6 mb-3">แพ็กเกจที่สมัครแล้ว</h2>

                    <div className="course-enroll-box rounded p-3">
                      {grantedEnrolls.map((en) => (
                        <div
                          key={en.id}
                          className="d-flex justify-content-between align-items-start gap-3 py-2 course-enroll-row"
                        >
                          <div>
                            <div className="fw-semibold">
                              {en.coursePackageLabel || en.coursePackage || "-"}
                            </div>
                            <div className="small text-muted">
                              {/* {en.amountText || formatBaht(en.amount || 0)} */}
                              {/* {en.paidAt ? ` • ชำระแล้ว ${formatPaidAt(en.paidAt)}` : ""} */}
                              {en.paidAt ? `ชำระแล้ว ${formatPaidAt(en.paidAt)}` : ""}
                            </div>
                          </div>

                          <span className="badge text-bg-success">สมัครแล้ว</span>
                        </div>
                      ))}

                      {pendingEnrolls.length > 0 && (
                        <div className="mt-3 pt-2">
                          <div className="small text-muted mb-2">
                            รายการที่อยู่ระหว่างดำเนินการ
                          </div>
                          {pendingEnrolls.map((en) => (
                            <div
                              key={en.id}
                              className="d-flex justify-content-between align-items-start gap-3 py-2"
                            >
                              <div>
                                <div className="fw-semibold">
                                  {en.coursePackageLabel || en.coursePackage || "-"}
                                </div>
                                <div className="small text-muted">
                                  {en.amountText || formatBaht(en.amount || 0)} บาท
                                </div>
                              </div>

                              <span className="badge text-bg-warning">
                                {en.paymentStatus || en.accessStatus || "pending"}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <h2 className="h6 mb-3">
                    {isAdditionalPurchase ? "เลือกแพ็กเกจที่ต้องการสมัครเพิ่ม" : "ข้อมูลคอร์สที่สมัครเรียน"}
                  </h2>

                  <div className="course-enroll-box rounded p-3">
                    <div className="fw-semibold">{displayCourseTitle}</div>

                    {!isThaibar ? (
                      <div className="mt-2 text-secondary">
                        ราคา {displayPriceText}
                      </div>
                    ) : availableThaibarPackages.length === 0 ? (
                      <div className="alert alert-success mt-3 mb-0">
                        คุณมีสิทธิครบทุกแพ็กเกจที่เปิดขายแล้ว
                      </div>
                    ) : (
                      <>
                        <div className="text-secondary small mt-1 mb-3">
                          กรุณาเลือกแพ็กเกจที่ต้องการสมัคร
                        </div>

                        <div
                          className={`course-enroll-package-list ${
                            submitted && validation.coursePackage ? "is-invalid" : ""
                          }`}
                        >
                          {availableThaibarPackageGroups.map((group, groupIndex) => (
                            <div
                              key={group.key}
                              className={groupIndex > 0 ? "mt-4" : ""}
                            >
                              <div className="fw-bold fs-5 mb-2">
                                {group.title}
                              </div>

                              {group.items.map((pkg) => {
                                const pkgRow = course?.packagePrices?.[pkg.value];
                                const pkgPriceText =
                                  typeof pkgRow === "object" && pkgRow?.priceText
                                    ? String(pkgRow.priceText)
                                    : formatBaht(getSelectedPrice(course, pkg.value));

                                return (
                                  <div className="form-check mb-2" key={pkg.value}>
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name="coursePackage"
                                      id={`pkg_${pkg.value}`}
                                      value={pkg.value}
                                      checked={form.coursePackage === pkg.value}
                                      onChange={(e) => setField("coursePackage", e.target.value)}
                                    />
                                    <label
                                      className="form-check-label w-100"
                                      htmlFor={`pkg_${pkg.value}`}
                                    >
                                      <div className="d-flex justify-content-between gap-3">
                                        <span>{pkg.label}</span>
                                        <span className="text-nowrap text-secondary">
                                          {pkgPriceText} บาท
                                        </span>
                                      </div>
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>

                        {submitted && validation.coursePackage && (
                          <div className="text-danger small mt-2">
                            {validation.coursePackage}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <div className="form-check">
                    <input
                      className={`form-check-input ${
                        submitted && validation.acceptTerms ? "is-invalid" : ""
                      }`}
                      type="checkbox"
                      id="acceptTerms"
                      checked={form.acceptTerms}
                      onChange={(e) => setField("acceptTerms", e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="acceptTerms">
                      ฉันยอมรับข้อกำหนดและนโยบายความเป็นส่วนตัว *
                    </label>
                    <div className="invalid-feedback">
                      {validation.acceptTerms}
                    </div>
                  </div>
                </div>

                {submitted && hasError && (
                  <div className="alert alert-warning">
                    กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน
                  </div>
                )}

                {submitError && (
                  <div className="alert alert-danger">{submitError}</div>
                )}

                <div className="d-grid gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loadingSubmit || (isThaibar && availableThaibarPackages.length === 0)}
                  >
                    {loadingSubmit ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        />
                        กำลังบันทึกข้อมูล...
                      </>
                    ) : isAdditionalPurchase ? (
                      "สมัครเพิ่มและชำระเงิน"
                    ) : (
                      "สมัครและชำระเงิน"
                    )}
                  </button>

                  {/* {isAdminUser && (
                    <button
                      type="button"
                      className="btn btn-outline-warning"
                      onClick={onAdminTestPromptPay}
                      disabled={loadingSubmit || (isThaibar && availableThaibarPackages.length === 0)}
                    >
                      {loadingSubmit
                        ? "กำลังสร้างรายการ..."
                        : "Admin ทดสอบสมัครและชำระเงิน QR-P"}
                    </button>
                  )} */}
                </div>

                <div className="text-secondary small mt-2">
                  เมื่อกดสมัคร จะเป็นการบันทึกข้อมูลการสมัครเรียนเพื่อดำเนินการชำระเงินต่อไป
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="card app-card course-enroll-card">
            <div className="card-body p-4">
              <div className="fw-semibold mb-3">สรุปรายการ</div>

              <div className="d-flex justify-content-between gap-3">
                <span className="text-secondary">คอร์ส</span>
                <span className="text-end">{displayCourseTitle}</span>
              </div>

              <div className="d-flex justify-content-between gap-3 mt-2">
                <span className="text-secondary">ประเภทการสมัคร</span>
                <span className="text-end">
                  {isAdditionalPurchase ? "สมัครเพิ่ม" : "สมัครครั้งแรก"}
                </span>
              </div>

              {isThaibar && (
                <div className="d-flex justify-content-between gap-3 mt-2">
                  <span className="text-secondary">แพ็กเกจ</span>
                  <span className="text-end">{selectedPackageLabel || "-"}</span>
                </div>
              )}

              <div className="d-flex justify-content-between gap-3 mt-2">
                <span className="text-secondary">ผู้สมัคร</span>
                <span className="text-end">{form.fullName?.trim() || "-"}</span>
              </div>

              <div className="d-flex justify-content-between gap-3 mt-2">
                <span className="text-secondary">เบอร์โทร</span>
                <span className="text-end">{form.phoneNumber?.trim() || "-"}</span>
              </div>

              <hr />

              <div className="d-flex justify-content-between align-items-center">
                <span className="text-secondary">ยอดชำระ</span>
                <span className="fw-semibold fs-5">{displayPriceText} บาท</span>
              </div>

              {isThaibar && grantedEnrolls.length > 0 && (
                <div className="text-secondary small mt-3">
                  ระบบจะสร้างรายการสมัครใหม่สำหรับแพ็กเกจที่เลือกเพิ่ม
                  โดยไม่กระทบสิทธิเดิมที่คุณมีอยู่แล้ว
                </div>
              )}

              <div className="text-secondary small mt-3">
                ชำระเงินและเข้าเรียนได้ทันที
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
